import styled from "styled-components";

// Main container
export const ReportsContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 8px;

  @media (min-width: 768px) {
    padding: 24px;
  }

  @media (min-width: 1024px) {
    padding: 32px;
  }
`;

// Page header
export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

export const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

// Dashboard Overview Section
export const DashboardSection = styled.div`
  margin-bottom: 32px;
`;

export const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;

  @media (min-width: 768px) {
    font-size: 22px;
    margin-bottom: 20px;
  }
`;

// Header info card
export const HeaderInfoCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;

  @media (min-width: 768px) {
    padding: 20px;
    margin-bottom: 24px;
  }
`;

export const HawkerCentreName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

export const ShopInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const ShopDetails = styled.div`
  font-size: 14px;
  color: #64748b;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

export const VerificationBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

export const BadgeIcon = styled.div<{ $verified: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => (props.$verified ? props.theme.colors.secondary : "#f59e0b")};
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px;
  font-weight: 600;
  cursor: ${(props) => (props.$verified ? "default" : "pointer")};
`;

export const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const Tooltip = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.text};
  color: ${({ theme }) => theme.colors.white};
  padding: 12px;
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  transition: opacity 0.2s, visibility 0.2s;
  margin-top: 8px;

  &::before {
    content: "";
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid ${({ theme }) => theme.colors.text};
  }
`;

// Today's Summary Cards
export const SummaryCardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 24px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
`;

export const SummaryCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (min-width: 768px) {
    padding: 20px;
  }
`;

export const SummaryIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: ${(props) => props.$color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  color: ${(props) => props.$color};
  font-size: 24px;
`;

export const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 4px;

  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

export const SummaryLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

// Statistics Overview
export const StatisticsSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

export const StatisticsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

export const StatisticsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

export const ToggleSwitch = styled.div`
  display: flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 4px;
`;

export const ToggleOption = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${(props) => (props.$active ? props.theme.colors.primary : "transparent")};
  color: ${(props) => (props.$active ? props.theme.colors.white : "#64748b")};

  &:hover {
    background-color: ${(props) => (props.$active ? props.theme.colors.primaryDark : "#e2e8f0")};
  }
`;

// Chart container
export const ChartContainer = styled.div`
  height: 300px;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    height: 350px;
  }
`;

// Summary cards for statistics
export const StatsSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
`;

export const StatsSummaryCard = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
`;

export const StatsSummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 4px;

  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

export const StatsSummaryLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

// Sales Report Section
export const SalesReportSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

export const SalesReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

export const SalesReportTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

// Line chart container
export const LineChartContainer = styled.div`
  height: 400px;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    height: 450px;
  }
`;

// Chart legend
export const ChartLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  margin-top: 16px;

  @media (min-width: 768px) {
    justify-content: flex-start;
  }
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const LegendColor = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${(props) => props.$color};
`;

export const LegendLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

// Loading and error states
export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #64748b;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #ef4444;
  text-align: center;
`;

export const RetryButton = styled.button`
  margin-top: 16px;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #d97706;
  }
`;

// New Mobile-First Reports Design Components
export const MobileReportsContainer = styled.div`
  width: 100%;
  min-height: 100vh;
`;

// Period Toggle Buttons
export const PeriodToggleContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (min-width: 768px) {
    gap: 12px;
    margin-bottom: 32px;
  }
`;

export const PeriodToggleButton = styled.button<{ $active: boolean }>`
  padding: 12px 24px;
  border: none;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${(props) => (props.$active ? props.theme.colors.primary : props.theme.colors.white)};
  color: ${(props) => (props.$active ? "#FEFAE3" : "#1E2F23")};
  border: 1px solid ${({ theme }) => theme.colors.underline};
`;

// Chart Section
export const ChartSection = styled.div`
  margin-bottom: 24px;
  background-color: ${({ theme }) => theme.colors.lightGray};
  padding: 16px;
`;

export const ChartLegendContainer = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  justify-content: center;

  @media (min-width: 768px) {
    justify-content: flex-start;
    margin-bottom: 24px;
  }
`;

export const LegendItemNew = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const LegendDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
`;

export const LegendText = styled.span`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

// Chart Container for Line Chart
export const MobileChartContainer = styled.div`
  height: 271px;

  @media (min-width: 768px) {
    height: 300px;
    margin-bottom: 20px;
  }

  @media (min-width: 1024px) {
    height: 350px;
  }
`;

// Total Orders Section
export const TotalOrdersSection = styled.div`
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.underline};
  color: ${({ theme }) => theme.colors.text};
`;

export const TotalOrdersTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 16px 0;
`;

export const TotalDiscountTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 16px;
`;

export const TotalOrdersValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  margin: 0;
`;

// Top-selling Items Section
export const TopSellingSection = styled.div`
  padding: 16px 0px 16px 0px;
`;

export const TopSellingList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }

  @media screen and (min-width: 768px) and (max-width: 1279px) {
      grid-template-columns: 1fr 1fr;
}
`;

export const TopSellingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.lightGray};
  padding: 0px 8px 0px 0px;
  margin-top: 8px;
  border-radius: 4px;
`;

export const ItemName = styled.div`
  font-size: 16px;
  color: #112609;
  font-weight: 700;
`;

export const ItemPrice = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
`;
export const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
`;

export const ItemQuantity = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 600;
  min-width: 40px;
  text-align: right;

  @media (min-width: 768px) {
    min-width: 50px;
  }
`;

export const NoItemsText = styled.div`
  text-align: center;
  color: #64748b;
  font-size: 14px;
  padding: 20px 0;

  @media (min-width: 768px) {
    font-size: 16px;
    padding: 24px 0;
  }
`;

export const NoChartDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 250px;
  color: #64748b;
`;
